const format = require('date-fns/format').default || require('date-fns/format');
const formatISO = require('date-fns/formatISO').default || require('date-fns/formatISO');

const requiredDataAccessMethods = ['getNamespaces', 'getRelations', 'getDefaultValueSelectors'];

// Used to lazily convert blocks that have changed name or group.
const blockConversions = {
    layout: {
        MeatballProfileLayout: {
            hNodeType: 'MeatballProfileLayout',
            hNodeTypeGroup: 'menu'
        }
    },
    profileMenu: {
        MeatballProfileLayout: {
            hNodeType: 'MeatballProfileLayout',
            hNodeTypeGroup: 'menu'
        }
    },
    dropDownMenu: {
        Menu: {
            hNodeTypeGroup: 'menu'
        },
        MenuDivider: {
            hNodeTypeGroup: 'menuSelection'
        },
        MenuItem: {
            hNodeTypeGroup: 'menuSelection'
        },
        SubMenu: {
            hNodeTypeGroup: 'menuSelection'
        },
        MenuShell: {
            hNodeTypeGroup: 'menuSelection'
        },
        MenuHeader: {
            hNodeTypeGroup: 'menuSelection'
        }
    }
};

// Map https://swagger.io/specification/#dataTypeFormat to hNodeTypes.
const dataTypeMapping = {
    long: {
        JavaScript: 'number',
        hNodeTypes: ['Integer'],
        defaultValue: 0
    },
    integer: {
        JavaScript: 'number',
        hNodeTypes: ['Integer', 'Slider', 'GeigerCounter'],
        defaultValue: 0
    },
    number: {
        //would love to do double or float as specified below, but swaggerize-routes doesn't seem to support that
        JavaScript: 'number',
        hNodeTypes: ['Currency'],
        defaultValue: 0.0
    },
    // double: {
    //     JavaScript: 'number',
    //     hNodeTypes: ['Currency'],
    //     defaultValue: 0.0
    // },
    // float: {
    //     JavaScript: 'number',
    //     hNodeTypes: ['Currency'],
    //     defaultValue: 0.0
    // },
    string: {
        JavaScript: 'string',
        defaultValue: '',
        formats: {
            email: {
                hNodeTypes: ['Email']
            },
            'long-text': {
                hNodeTypes: ['LongText']
            },
            'short-text': {
                hNodeTypes: ['ShortText', 'ShortEncodedText', 'FindRecord', 'MarkRecord', 'Barcode']
            },
            password: {
                hNodeTypes: ['Password', 'PasswordWithConfirm']
            },
            'date-time': {
                hNodeTypes: ['DatePicker', 'ReadOnlyDateTime']
            },
            generic: {
                hNodeTypes: ['ValueSearch', 'TextLabel', 'Link', 'ReadOnlyText']
            }
        }
    },
    boolean: {
        JavaScript: 'bool',
        hNodeTypes: ['Toggle'],
        defaultValue: false
    },
    array: {
        JavaScript: 'Array',
        defaultValue: [],
        formats: {
            string: {
                hNodeTypes: ['MultiPickerShortText']
            },
            object: {
                hNodeTypes: [
                    'DeferredDynamicFieldEntry',
                    'DynamicFieldEntry',
                    'EditableList',
                    'ElementGroups',
                    'MultiPickerDropDown',
                    'MultiPickerDropDownSearch',
                    'MultiUserPickerDropDownSearch',
                    'MultiPickerSensorGrid',
                    'MultiPickerTransactionGrid',
                    'ReadOnlyFormList',
                    'FormCardList',
                    'WorkflowActionList',
                    'WorkflowCriteriaPane'
                ]
            }
        }
    },
    object: {
        JavaScript: 'object',
        defaultValue: undefined,
        formats: {
            generic: {
                hNodeTypes: [
                    'DropDown',
                    'DropDownWithFilter',
                    'DropDownCriteria',
                    'DropDownSearch',
                    'FilePicker',
                    'Image',
                    'IntegerCriteria',
                    'JsonViewer',
                    'MultiPickerDropDownCriteria',
                    'NestedObject',
                    'NestedDetail',
                    'PageAccess',
                    'Range',
                    'ShortTextCriteria',
                    'WorkflowActionPane'
                ]
            },
            hierarchyParent: {
                hNodeTypes: ['HierarchyParent']
            },
            coordinates: {
                defaultValue: { firstCoordinate: 0, secondCoordinate: 0 },
                hNodeTypes: ['GpsCoordinates', 'Coordinates']
            }
        }
    }
};

const now = new Date();
const dateTimeFormats = ['PP p', 'Pp', 'PP', 'P', 'p', 'HH:mm:ss', 'ISO'];
const dateTimeFormatOptions = dateTimeFormats.map(f => {
    if (f === 'ISO') return [formatISO(now), f];

    return [format(now, f), f];
});

const roles = [
    ['New user', 'NEWUSER'],
    ['User', 'USER'],
    ['Admin', 'ADMIN'],
    ['Support', 'SUPPORT'],
    ['deity', 'DEITY']
];
const securityOptions = [
    ['new user', 'NEWUSER'],
    ['user', 'USER'],
    ['admin', 'ADMIN'],
    ['support', 'SUPPORT']
];
const platform = [
    ['native', 'NATIVE'],
    ['native without rfid', 'NONRFIDNATIVE'],
    ['web', 'WEB']
];

const buttonStyles = [
    ['Accent One', 'AccentOne'],
    ['Accent Two', 'AccentTwo'],
    ['Circle Add', 'CircleAdd'],
    ['Dark', 'Dark'],
    ['Light', 'Light'],
    ['Negative', 'Negative'],
    ['Positive', 'Positive'],
    ['Unstyled', 'unstyled'],
    ['In Line', 'InLine'],
    ['Icon Action', 'IconAction'],
    ['Big Icon Action', 'BigIconAction']
];

const buttonVariants = [
    ['Primary', 'primary'],
    ['Secondary', 'secondary'],
    ['Tertiary', 'tertiary'],
    ['In Line', 'inLine'],
    ['Link', 'link']
];

const componentSize = [
    ['Small', 'sm'],
    ['Large', 'lg']
];

const dataOperationVerbs = [
    ['Export', 'export'],
    ['Import', 'import'],
    ['Remove', 'remove'],
    ['Filter', 'filter'],
    ['New', 'new'],
    ['Mark', 'mark'],
    ['Save', 'save'],
    ['Get More', 'getMore']
];

// Previous dropdown options used for 'forAction' values.
const actionElementActions = [
    ['Nothing', 'nothing'],
    ['Cancel', 'cancel'],
    ['Change', 'change'],
    ['Clear Cache', 'clearCache'],
    ['Clear Session', 'clearSession'],
    ['Close', 'close'],
    ['Edit', 'edit'],
    ['Export', 'export'],
    ['Filter', 'filter'],
    ['Find record', 'findRecord'],
    ['Get more', 'getMore'],
    ['Logout', 'logout'],
    ['Mark', 'mark'],
    ['New', 'new'],
    ['Open', 'open'],
    ['Print', 'print'],
    // Order resuscitation (#1909): archives the active order and opens its clean replacement
    // under the same order number. Dispatches to doingRecreate_order_order, which calls
    // POST /api/order/order/{title}/copy. The button is destructive, so its metadata should
    // carry a confirmation prompt.
    ['Recreate', 'recreate'],
    ['Refresh', 'refresh'],
    ['Remove', 'remove'],
    // for non-CRUD level persisting outside of standard/formal form logic (which would use submit)
    // often accompanied by an "action" to specify more precise details
    // around the save operation due to it non-standard use.
    ['Save', 'save'],
    ['Set filter', 'setFilter'],
    ['Update', 'update'],
    ['View', 'view']
];

const actionElementActionsByNsRel = {
    dataOperationVerbs,
    application: {
        component: [['Open', 'open']],
        savedFilter: [['Remove', 'remove']],
        sharedFilter: [['Remove', 'remove']],
        reset: [['New', 'new']],
        runtime: [
            ['Clear Cache', 'clearCache'],
            ['Clear Session', 'clearSession']
        ]
    },
    print: {
        job: [
            ['Open', 'open'],
            ['New', 'new']
        ],
        template: [['New', 'new']]
    },
    transaction: {
        checkOut: [['New', 'new']],
        checkIn: [['New', 'new']]
    },
    inventory: {
        inventory: [
            ['New', 'new'],
            ['Filter', 'filter']
        ],
        transaction: [['Export', 'export']]
    },
    sensor: {
        tag: [['New', 'new']],
        service: [['Change', 'change']]
    },
    metaui: {
        captionOverrides: [['Edit', 'edit']]
    },
    identity: {
        user: [['New', 'new', 'Filter', 'filter']]
    },
    rulesEngine: {
        bigQueryBacking: [['New', 'new']]
    },
    all: {
        all: actionElementActions
    }
};

const formActions = [
    ['Nothing', 'nothing'],
    ['Cancel', 'cancel'],
    ['Close', 'close'],
    ['Copy', 'copy'],
    ['Edit', 'edit'],
    ['View', 'view'],
    ['New', 'new'],
    ['Print', 'print'],
    ['Remove', 'remove'],
    //Submit is captured by the big form state machine,
    //which translates it into `create` or `update` CRUD verbs as applicable
    ['Submit', 'submit'],
    ['Refresh', 'refresh'],
    ['Create', 'create'], // unusual - only for things like password resets. A subtype needs to be required.
    // Serialized Each gen-3 create panes: a MenuItem subtype is echoed as the activation verb,
    // and the DetailPane's forAction must be able to hold the same value to match it.
    ['New (Auto Serialize)', 'newAutoSerial'],
    ['New (From RFID Tag)', 'newFromTag']
];

const scanActions = [
    ['Submit (form)', 'submit'],
    ['Save (non-form)', 'save'],
    ['Mark', 'mark'],
    ['Find record', 'findRecord']
];

const greedyLayoutOptions = [
    ['First', 'FIRST'],
    ['Middle', 'MIDDLE'],
    ['Last', 'LAST']
];

const dateOffsetUnit = [
    ['Day', 'days'],
    ['Month', 'months'],
    ['Year', 'years'],
    ['Quarter', 'quarters'],
    ['Week', 'weeks'],
    ['Hour', 'hours']
];

const viewPorts = [
    ['Mobile', 'MOBILE'],
    ['Tablet', 'TABLET'],
    ['Desktop', 'DESKTOP']
];

const workflowActions = [
    ['Activate GPO', 'send_deviceManagement_gpo'],
    ['Send Email', 'send_notification_email'],
    ['Send RESTfull Push Notification', 'send_notification_http'],
    ['Forward over MQTT', 'forward'],
    ['Update', 'update'],
    ['Restrict Users', 'restrict_identity_user'],
    ['Restrict Devices', 'restrict_deviceManagement_device']
];

const unknownTagActions = [
    ['None', 'none'],
    ['Error', 'error'],
    ['Remove', 'remove'],
    ['Remove and Error', 'removeAndError']
];

const allowedNamespaceRelationExceptions = { sensor: { read: true } };
const applicationNamespaceAdditions = {
    relations: [
        { prettyName: 'Component', title: 'component', accessContext: { get: true } },
        { prettyName: 'Use Case', title: 'useCase', accessContext: { get: true } },
        { prettyName: 'Notification', title: 'notification', accessContext: { get: true } },
        { prettyName: 'Route', title: 'route', accessContext: { get: true } },
        { prettyName: 'Runtime', title: 'runtime', accessContext: { get: true } },
        { prettyName: 'Progress', title: 'progress', accessContext: { get: true } },
        { prettyName: 'User', title: 'user', accessContext: { get: true } },
        { prettyName: 'Session', title: 'session', accessContext: { get: true } },
        { prettyName: 'Network', title: 'network', accessContext: { get: true } },
        { prettyName: 'Predefined Records', title: 'predefinedRecords', accessContext: { get: true } },
        { prettyName: 'RFID Enabled Relation', title: 'rfidEnabledRelation', accessContext: { get: true } }
    ]
};

const actionSubtypeTargets = {
    save: {
        filter: {
            namespace: 'application',
            relation: 'savedFilter',
            action: 'create'
        },
        sharedFilter: {
            namespace: 'application',
            relation: 'sharedFilter',
            action: 'create'
        }
    }
};

// \u00A0 avoids the Blockly suffix matcher extracting the common word as a label
// (the suffix label extraction creates a confusing and longer block layout)
const dataRetention = {
    scopeOptions: [
        ['Retain\u00A0by Page/Request', 'local'],
        ['Retain\u00A0by Route', 'perRoute'],
        ['Retain\u00A0Globally', 'global']
    ],
    durationOptions: [
        ['Retain\u00A0always', 'always'],
        ['Retain\u00A0during Request', 'request'],
        ['Retain\u00A0for Page Session', 'pageLoad'],
        ['Retain\u00A0for User Session', 'userSession'],
        ['DEPRECATED - Retain\u00A0for Session', 'session']
    ],
    visibilityOptions: [
        ['Retain\u00A0in Browser', 'perBrowser'],
        ['Retain\u00A0for User (not implemented yet)', 'perUser'],
        ['Retain\u00A0for Tenant (not implemented yet)', 'perTenant']
    ]
};

// Only count is implemented at the moment.  Leaving the others commented out to give
// a better sense of what this is for.
const dataAggregationOps = [
    ['Count', 'count'],
    ['Sum (not implemented)', 'sum'],
    ['Average (not implemented)', 'average'],
    ['Min (not implemented)', 'min'],
    ['Max (not implemented)', 'max']
];

const textSizes = [
    ['Extra Small', 'xs'],
    ['Small (default)', 'sm'],
    ['Medium', 'md'],
    ['Large', 'lg'],
    ['Extra Large', 'xl']
];

const textStyles = [
    ['Medium', 'medium'],
    ['Regular (default)', 'regular'],
    ['Semibold', 'semibold'],
    ['Bold', 'bold']
];

const systemRoles = {
    USER: '177700000000000100000001',
    ADMIN: '177700000000000100000002',
    SYSTEM: '177700000000000100000003'
};

// Shared choices keep Blockly's settings and the chart's controls in agreement.
const timeSeriesControls = {
    ranges: [
        { value: 'thirtyDays', label: '30 days', duration: 30 * 24 * 60 * 60 * 1000, interval: 'day' },
        { value: 'sevenDays', label: '7 days', duration: 7 * 24 * 60 * 60 * 1000, interval: 'hour' },
        { value: 'oneDay', label: '24 hours', duration: 24 * 60 * 60 * 1000, interval: 'hour' },
        { value: 'oneHour', label: '1 hour', duration: 60 * 60 * 1000, interval: 'fiveMinutes' }
    ],
    groupings: [
        { value: 'day', label: 'Daily', duration: 24 * 60 * 60 * 1000 },
        { value: 'hour', label: 'Hourly', duration: 60 * 60 * 1000 },
        { value: 'fiveMinutes', label: '5 minutes', duration: 5 * 60 * 1000 }
    ],
    // Limit the number of time buckets before requesting an impractically large chart.
    maxBuckets: 10000
};

module.exports = {
    actionElementActions,
    actionElementActionsByNsRel,
    actionSubtypeTargets,
    allowedNamespaceRelationExceptions,
    applicationNamespaceAdditions,
    buttonStyles,
    buttonVariants,
    componentSize,
    blockConversions,
    dataTypeMapping,
    dataRetention,
    dateOffsetUnit,
    dateTimeFormatOptions,
    formActions,
    scanActions,
    greedyLayoutOptions,
    platform,
    requiredDataAccessMethods,
    roles,
    securityOptions,
    textSizes,
    textStyles,
    unknownTagActions,
    viewPorts,
    workflowActions,
    dataAggregationOps,
    systemRoles,
    timeSeriesControls
};
